<template>
  <g class="dn-node" v-tooltip.top="tooltipConfig">
    <circle
      :cx="cx"
      :cy="cy"
      :r="radius"
      :class="{
        'dn-circle': true,
        'dn-circle--nomodel': isNoModel,
        'dn-circle--dashed': !isNoModel && betaSign < 0,
      }"
      :style="isNoModel ? {} : { fill: wash, stroke: color }"
    />
    <template v-if="!isNoModel">
      <text
        :x="cx"
        :y="cy - 14"
        class="dn-label"
        text-anchor="middle"
        :style="{ fill: color }"
      >
        {{ label }}
      </text>
      <text :x="cx" :y="cy + 12" class="dn-value" text-anchor="middle" :style="{ fill: color }">
        {{ r2 }}%
      </text>
      <text
        v-if="showWarningIcon"
        :x="cx + radius - 4"
        :y="cy - radius + 4"
        class="dn-warning"
        text-anchor="middle"
        v-tooltip.top="warningsText"
      >
        !
      </text>
    </template>
    <template v-else>
      <text :x="cx" :y="cy - 8" class="dn-label" text-anchor="middle">
        {{ label }}
      </text>
      <text :x="cx" :y="cy + 12" class="dn-nomodel-text" text-anchor="middle">
        Sin modelo
      </text>
    </template>
  </g>
</template>

<script>
import {
  nonConstantCoefficients,
  roundRSquaredAdj,
  nodeSize,
  confidenceLabel,
  NOMODEL_RADIUS,
} from "./regression-helpers";

const CONFIDENCE_COLORS = {
  "alta confianza": { bg: "#d9ead3", border: "#c3eab5", ink: "#3d6b3f" },
  "confianza moderada": { bg: "#fff2cc", border: "#f6db8a", ink: "#8a6d1f" },
  "confianza baja": { bg: "#facca5", border: "#f6b884", ink: "#95591f" },
};

export default {
  name: "DomainNode",
  props: {
    analysis: {
      type: Object,
      default: null,
    },
    label: {
      type: String,
      required: true,
    },
    cx: {
      type: Number,
      required: true,
    },
    cy: {
      type: Number,
      required: true,
    },
    color: {
      type: String,
      default: "#6a6a6a",
    },
    wash: {
      type: String,
      default: "#f3eaf9",
    },
  },
  computed: {
    isNoModel() {
      return !this.analysis || this.analysis.no_model === true;
    },
    mainCoefficient() {
      const rows = nonConstantCoefficients(this.analysis);
      return rows.length ? rows[0] : null;
    },
    betaSign() {
      return this.mainCoefficient ? this.mainCoefficient.beta : 0;
    },
    betaText() {
      if (!this.mainCoefficient || this.mainCoefficient.beta == null) return "—";
      const b = this.mainCoefficient.beta;
      return (b >= 0 ? "+" : "−") + Math.abs(b).toFixed(2);
    },
    r2() {
      return roundRSquaredAdj(this.analysis);
    },
    radius() {
      return this.isNoModel ? NOMODEL_RADIUS : nodeSize(this.r2);
    },
    n() {
      return this.analysis && this.analysis.n != null ? this.analysis.n : null;
    },
    confidence() {
      if (!this.mainCoefficient) return null;
      return confidenceLabel(this.mainCoefficient.p_value);
    },
    confidenceChip() {
      const meta = CONFIDENCE_COLORS[this.confidence];
      if (!meta) return "";
      return (
        '<div class="rn-tt-row"><span>Confiabilidad</span><span class="rn-tt-chip" style="background:' +
        meta.bg +
        ";border-color:" +
        meta.border +
        ";color:" +
        meta.ink +
        '">' +
        this.confidence +
        "</span></div>"
      );
    },
    warnings() {
      return (this.analysis && this.analysis.warnings) || [];
    },
    showWarningIcon() {
      return !this.isNoModel && this.warnings.length > 0;
    },
    warningsText() {
      return this.warnings.join(" ");
    },
    tooltipConfig() {
      if (this.isNoModel) {
        const content =
          "<b>" +
          this.label +
          '</b><div class="rn-tt-sub">Sin modelo — muestra insuficiente</div>' +
          (this.n != null
            ? '<div class="rn-tt-row"><span>n efectivo</span><span>' + this.n + "</span></div>"
            : "") +
          (this.warnings.length ? '<div class="rn-tt-sub">' + this.warningsText + "</div>" : "");
        return { content: content, html: true, classes: ["rn-tooltip"] };
      }
      const sub = this.n != null ? "R² ajustado " + this.r2 + "% · n=" + this.n : "R² ajustado " + this.r2 + "%";
      const content =
        "<b>" +
        this.label +
        "</b>" +
        '<div class="rn-tt-sub">' +
        sub +
        "</div>" +
        '<div class="rn-tt-row"><span>β estandarizado</span><span>' +
        this.betaText +
        "</span></div>" +
        this.confidenceChip;
      return { content: content, html: true, classes: ["rn-tooltip"] };
    },
  },
};
</script>

<style lang="scss" scoped>
.dn-circle {
  fill: #f3eaf9;
  stroke: #6a6a6a;
  stroke-width: 1.5;
  stroke-dasharray: none;
}

.dn-circle--dashed {
  stroke-dasharray: 4 3;
}

@media (prefers-reduced-motion: no-preference) {
  .dn-circle {
    transition: r 0.4s cubic-bezier(0.34, 1.3, 0.64, 1),
      cx 0.4s cubic-bezier(0.34, 1.3, 0.64, 1), cy 0.4s cubic-bezier(0.34, 1.3, 0.64, 1);
  }
}

.dn-circle--nomodel {
  fill: #f4cdcd;
  fill-opacity: 0.45;
  stroke: #f6adad;
  stroke-dasharray: 4 3;
}

.dn-node {
  outline: none;
}

.dn-node:focus-visible {
  outline: 2px solid #2a78d6;
  outline-offset: 2px;
}

.dn-label {
  font-family: "Poppins", sans-serif;
  font-size: 13.5px;
  font-weight: 700;
  fill: #363636;
}

.dn-value {
  font-family: "Poppins", sans-serif;
  font-size: 17px;
  font-weight: 700;
  fill: #363636;
}

.dn-warning {
  font-size: 12px;
  fill: #e0a640;
  cursor: help;
  outline: none;
}

.dn-warning:focus-visible {
  outline: 2px solid #2a78d6;
  outline-offset: 2px;
}

.dn-nomodel-text {
  font-family: "Inter", sans-serif;
  font-size: 10px;
  fill: #a12a2a;
}
</style>

<!-- Non-scoped: v-tooltip renders at <body>, así que el tema del tooltip
     de esta Red debe ser global (mismo patrón que .critical-tooltip en
     CriticalCasesCard.vue). Compartido con IntralaboralNode.vue/RelationsNetwork.vue
     (mismas clases .rn-tt-*). -->
<style lang="scss">
.rn-tooltip {
  .tooltip-inner {
    background: #241f2e;
    color: #fbfaf9;
    border-radius: 8px;
    padding: 9px 12px;
    font-size: 12px;
    line-height: 1.45;
    max-width: 240px;
    text-align: left;

    b {
      display: block;
      font-weight: 700;
      margin-bottom: 2px;
      font-family: "Poppins", sans-serif;
    }
  }
  .tooltip-arrow {
    border-color: #241f2e;
  }
}

.rn-tt-sub {
  color: #948da3;
  font-size: 11px;
}
.rn-tt-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 5px;
}
.rn-tt-chip {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 100px;
  border: 1px solid;
}
</style>



// WEBPACK FOOTER //
// src/components/frpm/components/variables-prioritarias/DomainNode.vue